/**
 * The analytics charts. Each has one series in one hue, so identity never
 * rests on colour, and each answers one question:
 *
 *   DistributionChart – how are students spread across score bands?
 *   TrendChart        – is performance going up or down over time?
 *   ComparisonChart   – how do classes / lessons / subjects compare on a metric?
 *
 * Percentage axes are fixed at 0–100 so two charts are never read against
 * different scales.
 */

import type { ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatPercent } from '../../lib/analytics/metrics';

const SERIES = 'var(--chart-1)';
const AXIS_TICK = { fill: 'var(--muted-foreground)', fontSize: 11 };
const GRID = 'var(--border)';

function TooltipBox({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-panel">
      <p className="mb-0.5 font-medium text-foreground">{title}</p>
      <div className="text-muted-foreground">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function DistributionChart({
  bands,
  height = 220,
  unit = 'student',
}: {
  bands: { label: string; count: number }[];
  height?: number;
  unit?: string;
}) {
  const total = bands.reduce((s, b) => s + b.count, 0);
  return (
    <figure aria-label={`Score distribution: ${bands.map((b) => `${b.label}%: ${b.count}`).join(', ')}`}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={bands} margin={{ top: 8, right: 4, left: -16, bottom: 0 }} barCategoryGap="22%">
          <CartesianGrid vertical={false} stroke={GRID} strokeOpacity={0.6} />
          <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID }} unit="%" />
          <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} width={40} />
          <Tooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.4 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const b = payload[0].payload as { label: string; count: number };
              return (
                <TooltipBox title={`${b.label}%`}>
                  {b.count} {unit}
                  {b.count === 1 ? '' : 's'}
                  {total > 0 ? ` · ${Math.round((b.count / total) * 100)}% of ${total}` : ''}
                </TooltipBox>
              );
            }}
          />
          <Bar dataKey="count" fill={SERIES} radius={[4, 4, 0, 0]} maxBarSize={56} />
        </BarChart>
      </ResponsiveContainer>
    </figure>
  );
}

// ---------------------------------------------------------------------------

export interface TrendDatum {
  /** Epoch ms, or any label for the x axis. */
  x: number | string;
  value: number;
  /** Tooltip line under the value, e.g. "12 submissions". */
  note?: string;
  /** Tooltip title; defaults to a formatted date. */
  title?: string;
}

const shortDate = (ms: number) => new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

export function TrendChart({
  data,
  height = 240,
  passThreshold,
  valueLabel = 'Average score',
}: {
  data: TrendDatum[];
  height?: number;
  /** Draws the pass mark as a reference line. */
  passThreshold?: number;
  valueLabel?: string;
}) {
  const numericX = data.every((d) => typeof d.x === 'number');
  return (
    <figure aria-label={`${valueLabel} over time`}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID} strokeOpacity={0.6} />
          <XAxis
            dataKey="x"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: GRID }}
            tickFormatter={(v) => (numericX ? shortDate(v as number) : String(v))}
            minTickGap={24}
          />
          <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={AXIS_TICK} tickLine={false} axisLine={false} width={40} unit="%" />
          {typeof passThreshold === 'number' && (
            <ReferenceLine
              y={passThreshold}
              stroke="var(--muted-foreground)"
              strokeDasharray="4 4"
              strokeOpacity={0.6}
              label={{ value: `Pass ${passThreshold}%`, position: 'insideTopRight', fill: 'var(--muted-foreground)', fontSize: 10 }}
            />
          )}
          <Tooltip
            cursor={{ stroke: 'var(--muted-foreground)', strokeOpacity: 0.4 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload as TrendDatum;
              return (
                <TooltipBox title={d.title ?? (typeof d.x === 'number' ? shortDate(d.x) : d.x)}>
                  <span className="font-medium text-foreground">{formatPercent(d.value)}</span> {valueLabel.toLowerCase()}
                  {d.note && <div>{d.note}</div>}
                </TooltipBox>
              );
            }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={SERIES}
            strokeWidth={2}
            dot={{ r: 4, fill: SERIES, stroke: 'var(--card)', strokeWidth: 2 }}
            activeDot={{ r: 5, stroke: 'var(--card)', strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </figure>
  );
}

// ---------------------------------------------------------------------------

export interface ComparisonDatum {
  key: string;
  label: string;
  value: number | null;
  note?: string;
}

/** Horizontal bars, so long class and lesson names stay readable. */
export function ComparisonChart({
  data,
  valueLabel,
  onSelect,
}: {
  data: ComparisonDatum[];
  valueLabel: string;
  onSelect?: (key: string) => void;
}) {
  const plotted = data.filter((d) => d.value !== null) as (ComparisonDatum & { value: number })[];
  const height = Math.max(120, plotted.length * 36 + 32);
  return (
    <figure aria-label={`${valueLabel} by group`}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={plotted} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }} barCategoryGap="28%">
          <CartesianGrid horizontal={false} stroke={GRID} strokeOpacity={0.6} />
          <XAxis type="number" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID }} unit="%" />
          <YAxis
            type="category"
            dataKey="label"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={112}
            tickFormatter={(v: string) => (v.length > 16 ? `${v.slice(0, 15)}…` : v)}
          />
          <Tooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.4 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload as ComparisonDatum;
              return (
                <TooltipBox title={d.label}>
                  <span className="font-medium text-foreground">{formatPercent(d.value)}</span> {valueLabel.toLowerCase()}
                  {d.note && <div>{d.note}</div>}
                </TooltipBox>
              );
            }}
          />
          <Bar
            dataKey="value"
            fill={SERIES}
            radius={[0, 4, 4, 0]}
            maxBarSize={22}
            cursor={onSelect ? 'pointer' : undefined}
            onClick={onSelect ? (d: any) => onSelect(d?.payload?.key ?? d?.key) : undefined}
          />
        </BarChart>
      </ResponsiveContainer>
    </figure>
  );
}

/** A tiny trend line for a table row. Decorative; the row states the figures. */
export function Sparkline({ values, width = 72, height = 22 }: { values: number[]; width?: number; height?: number }) {
  if (values.length < 2) return <span className="text-xs text-muted-foreground">—</span>;
  const step = width / (values.length - 1);
  const y = (v: number) => height - 2 - (Math.max(0, Math.min(100, v)) / 100) * (height - 4);
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden className="overflow-visible">
      <polyline points={points} fill="none" stroke={SERIES} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={(values.length - 1) * step} cy={y(values[values.length - 1])} r={2.5} fill={SERIES} />
    </svg>
  );
}
